import { Gender } from '@dokucoach/common-types';
import { makeStyles } from '@material-ui/core';
import Collapse from '@material-ui/core/Collapse';
import restoreSvg from 'assets/icon/restore.svg';
import absentIcon from 'assets/icon/signout.svg';
import xSvg from 'assets/icon/x.svg';
import { BoxSubmenu, EntityListButton } from 'components/Layout/components/AsideMenuList/style';
import routePaths from 'constants/route-paths';
import React, { MouseEvent, ReactNode, useLayoutEffect, useRef } from 'react';
import { Image } from 'react-bootstrap';
import SVG from 'react-inlinesvg';
import { useHistory } from 'react-router';
import { matchPath, useLocation } from 'react-router-dom';
import theme from 'style/theme';
import { AvatarImageType, getAvatarImage } from 'utils/getAvatarImage';
import { getPath } from 'utils/path';

const useStyles = makeStyles(() => ({
  deleteRestoreButton: {
    outline: 'none',
    border: 'none',
    background: 'none',
    height: '24px',
    width: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.colors.malibu,
    margin: '0 4px',
    '&:hover': {
      color: theme.colors.eastBay,
    },
  },
}));

type Props = {
  avatarImage?: { svg: AvatarImageType } | { url: string };
  label: string;
  id: string;
  description: string;
  gender?: Gender;
  warning: number;
  tabPaneRef: any;
  children?: ReactNode;
  routePath: string;
  showDelete?: boolean;
  showRestore?: boolean;
  isDeleted?: boolean;
  isActive?: boolean;
  isAbsent?: boolean;
  onDelete?: () => void;
  onRestore?: () => void;
};

const AsideListItem = ({
  avatarImage = { svg: AvatarImageType.DEFAULT },
  label,
  description,
  gender,
  id,
  tabPaneRef,
  children,
  routePath,
  showRestore = false,
  showDelete = false,
  isDeleted = false,
  isAbsent = false,
  onDelete,
  onRestore,
}: Props) => {
  const history = useHistory();
  const location = useLocation();
  const classes = useStyles();

  const userPaths = matchPath(location.pathname, {
    path: routePath,
  }) as { params: { entityId: string } };

  const params = {
    entityId: userPaths?.params?.entityId,
  } as { entityId: string };

  const active = params?.entityId === id;

  const currentRef = useRef<HTMLLIElement>(null);

  useLayoutEffect(() => {
    if (currentRef && currentRef.current && active) {
      setTimeout(() => {
        const primaryMenuY = currentRef.current ? currentRef.current?.offsetTop : 0;
        tabPaneRef.current.scrollTo(0, primaryMenuY);
      }, 500);
    }
  }, [active, tabPaneRef]);

  const onClick = (e: MouseEvent<HTMLDivElement>) => {
    let path = '';
    if (history.location.pathname.includes(routePaths.DELETED)) {
      path = routePaths.DELETED;
    } else if (history.location.pathname.includes(routePaths.INACTIVE)) {
      path = routePaths.INACTIVE;
    } else if (history.location.pathname.includes(routePaths.ABSENT)) {
      path = routePaths.ABSENT;
    } else if (history.location.pathname.includes(routePaths.APPLICANT)) {
      path = routePaths.APPLICANT;
    }

    const paths = [routePath, routePaths.DATA_GENERAL];
    if (path.length) paths.push(path);

    history.push(getPath(paths, { entityId: id }));
    e.stopPropagation();
    return;
  };

  const onDeleteRestoreClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    onRestore && onRestore();
  };

  const onDeleteClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    onDelete && onDelete();
  };

  return (
    <li
      id={id}
      className={`menu-item menu-item-submenu px-3 ${active && 'menu-item-active menu-item-open'}`}
      data-menu-toggle="hover"
      ref={currentRef}
    >
      <EntityListButton
        className={`menu-link my-1 menu-toggle ${!gender && 'menu-border'}`}
        style={{
          paddingTop: '5px',
          paddingBottom: '5px',
          paddingLeft: '30px',
        }}
        onClick={onClick}
      >
        {gender && (
          <span className="svg-icon menu-icon m-2">
            <Image
              src={
                ('url' in avatarImage && avatarImage.url) ||
                ('svg' in avatarImage && getAvatarImage(gender, avatarImage.svg)) ||
                undefined
              }
              width="35"
              rounded
            />
          </span>
        )}
        <span className="menu-text">
          <div className={`header header-active`}>{label}</div>
          <div className="sub-header">{description}</div>
        </span>
        {isAbsent && (
          <button disabled className={classes.deleteRestoreButton}>
            <SVG src={absentIcon} />
          </button>
        )}
        {showRestore && active && (
          <button
            className={classes.deleteRestoreButton}
            data-test-id="active-inactive-button"
            onClick={(event) => onDeleteRestoreClick(event)}
          >
            <SVG src={isDeleted ? restoreSvg : xSvg} />
          </button>
        )}
        {showDelete && active && (
          <button
            className={classes.deleteRestoreButton}
            data-test-id="delete-button"
            onClick={(event) => onDeleteClick(event)}
          >
            <SVG src={xSvg} />
          </button>
        )}
      </EntityListButton>

      <Collapse in={active} timeout={500}>
        <BoxSubmenu>{children}</BoxSubmenu>
      </Collapse>
    </li>
  );
};

export default AsideListItem;
